import { getCallable } from "../../_sdk/client";
import { apiName, endpoints } from "./endpoints";
import type {
    GetWorkspaceResourcesRequest, GetWorkspaceResourcesResponse,
    UpdateWorkspaceResourceRequest, UpdateWorkspaceResourceResponse,
    DeleteWorkspaceResourceRequest, DeleteWorkspaceResourceResponse,
    CreateWorkspaceResourceRequest, CreateWorkspaceResourceResponse,
    GetUserPermissionsToResourceRequest, GetUserPermissionsToResourceResponse,
    UpdateWorkspaceResourceUsersRequest, UpdateWorkspaceResourceUsersResponse,
    GetWorkspacePermissionsToResourceRequest, GetWorkspacePermissionsToResourceResponse,
    GetCollaborationInvitesToResourceRequest, GetCollaborationInvitesToResourceResponse
} from "./types";

export { apiName };

const getWorkspaceResources = getCallable<GetWorkspaceResourcesRequest, GetWorkspaceResourcesResponse>(
    apiName,
    "getWorkspaceResources",
    {
        ...endpoints.getWorkspaceResources,
        composer: request => {
            const searchParams = new URLSearchParams();
            if (request.resources) {
                searchParams.append("resources", JSON.stringify(request.resources));
            }

            return {
                url: `/workspaces/${request.workspaceId}/resources?${searchParams.toString()}`,
                options: {
                    method: "GET"
                }
            };
        }
    }
);

const updateWorkspaceResource = getCallable<UpdateWorkspaceResourceRequest, UpdateWorkspaceResourceResponse>(
    apiName,
    "updateWorkspaceResource",
    {
        ...endpoints.updateWorkspaceResource,
        composer: request => {
            return {
                url: `/workspaces/${request.workspaceId}/resources/${request.resourceType}/${request.resourceId}`,
                options: {
                    method: "PUT",
                    body: JSON.stringify(request)
                }
            };
        }
    }
);

const deleteWorkspaceResource = getCallable<DeleteWorkspaceResourceRequest, DeleteWorkspaceResourceResponse>(
    apiName,
    "deleteWorkspaceResource",
    {
        ...endpoints.deleteWorkspaceResource,
        composer: request => {
            return {
                url: `/workspaces/${request.workspaceId}/resources/${request.resourceType}/${request.resourceId}`,
                options: { method: "DELETE" }
            };
        }
    }
);

const createWorkspaceResource = getCallable<CreateWorkspaceResourceRequest, CreateWorkspaceResourceResponse>(
    apiName,
    "createWorkspaceResource",
    {
        ...endpoints.createWorkspaceResource,
        composer: request => {
            return {
                url: `/workspaces/${request.workspaceId}/resources`,
                options: { method: "POST", body: JSON.stringify(request) }
            };
        }
    }
);

const getUserPermissionsToResource = getCallable<GetUserPermissionsToResourceRequest, GetUserPermissionsToResourceResponse>(
    apiName,
    "getUserPermissionsToResource",
    {
        ...endpoints.getUserPermissionsToResource,
        composer: request => {
            const searchParams = new URLSearchParams();
            if (request.uid) {
                searchParams.append("uid", request.uid);
            }

            return {
                url: `/workspaces/${request.workspaceId}/resources/${encodeURIComponent(request.resourceType)}/${encodeURIComponent(request.resourceId)}/user-permissions?${searchParams.toString()}`,
                options: {
                    method: "GET"
                }
            };
        }
    }
);

const updateWorkspaceResourceUsers = getCallable<UpdateWorkspaceResourceUsersRequest, UpdateWorkspaceResourceUsersResponse>(
    apiName,
    "updateWorkspaceResourceUsers",
    {
        ...endpoints.updateWorkspaceResourceUsers,
        composer: request => {
            return {
                url: `/workspaces/${request.workspaceId}/resources/${request.resourceType}/${request.resourceId}/users`,
                options: {
                    method: "PUT",
                    body: JSON.stringify(request)
                }
            };
        }
    }
);

const getWorkspacePermissionsToResource = getCallable<GetWorkspacePermissionsToResourceRequest, GetWorkspacePermissionsToResourceResponse>(
    apiName,
    "getWorkspacePermissionsToResource",
    {
        ...endpoints.getWorkspacePermissionsToResource,
        composer: request => {
            return {
                url: `/workspaces/${request.workspaceId}/resources/${request.resourceType}/${request.resourceId}/permissions`,
                options: {
                    method: "GET"
                }
            };
        }
    }
);

const getCollaborationInvitesToResource = getCallable<GetCollaborationInvitesToResourceRequest, GetCollaborationInvitesToResourceResponse>(
    apiName,
    "getCollaborationInvitesToResource",
    {
        ...endpoints.getCollaborationInvitesToResource,
        composer: request => {
            return {
                url: `/workspaces/${request.workspaceId}/resources/${request.resourceType}/${request.resourceId}/invites`,
                options: {
                    method: "GET"
                }
            };
        }
    }
);

export const callables = {
    getWorkspaceResources,
    updateWorkspaceResource,
    deleteWorkspaceResource,
    createWorkspaceResource,
    getUserPermissionsToResource,
    updateWorkspaceResourceUsers,
    getWorkspacePermissionsToResource,
    getCollaborationInvitesToResource
};
